import { ReactNode } from "react";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";

/**
 * Configuration for video tracking events.
 * Allows customizing the event_name and widget values sent to analytics.
 */
export type VideoTrackingConfig = {
    /** Event name for play action (default: "pqp_interaction") */
    playEventName?: string;
    /** Event name for pause/stop action (default: "pqp_interaction") */
    stopEventName?: string;
    /** Widget identifier (default: "testimonials_v3") */
    widget?: string;
    /** Include the current video sound state in analytics metadata. */
    includeVslSound?: boolean;
};

/** Default tracking configuration for pqp/hormone-imbalance usage */
export const DEFAULT_VIDEO_TRACKING_CONFIG: Required<VideoTrackingConfig> = {
    playEventName: GTM_EVENT_NAMES.PQP_INTERACTION,
    stopEventName: GTM_EVENT_NAMES.PQP_INTERACTION,
    widget: "testimonials_v3",
    includeVslSound: false,
};

export type VideoTopTestimonialV3 = {
    type: "video-top";
    mp4: string;
    hls: string;
    poster: string;
    text: ReactNode;
    author: string;
    smallText?: boolean;
    /** Per-slide override for the video element classes (e.g. object-position). Merged over the defaults. */
    videoClassName?: string;
};

export type VideoBottomTestimonialV3 = {
    type: "video-bottom";
    mp4: string;
    hls: string;
    poster: string;
    text: ReactNode;
    author: string;
    /** Per-slide override for the video element classes (e.g. object-position). Merged over the defaults. */
    videoClassName?: string;
};

export type InfoTestimonialV3 = {
    type: "info";
    text: ReactNode | ReactNode[];
    author: string;
    photo: string;
    photoAlt?: string;
};

export type DoubleInfoTestimonialV3 = {
    type: "double-info";
    feedbacks: {
        text: ReactNode;
        author: string;
        photo: string;
        photoAlt?: string;
    }[];
    /** Optional override for the feedback text classes (e.g. font size). Merged over the defaults. */
    textClassName?: string;
};

export type TestimonialV3 = VideoTopTestimonialV3 | VideoBottomTestimonialV3 | InfoTestimonialV3 | DoubleInfoTestimonialV3;

export const testimonialsV3: TestimonialV3[] = [
    {
        type: "video-top",
        mp4: "https://res.cloudinary.com/dirscuuhp/video/upload/v1765923395/UGC%20Videos/Marcia_O_cut_rm26cj.mp4",
        hls: "https://res.cloudinary.com/dirscuuhp/video/upload/v1765923395/UGC%20Videos/Marcia_O_cut_rm26cj.m3u8",
        poster: "https://res.cloudinary.com/dirscuuhp/image/upload/v1748268371/UGC%20Videos/Marcia_O_jaciqf.webp",
        text: "I’ve lost weight, my energy is back, and my depression is gone...",
        author: "MARCIA",
    },
    {
        type: "video-bottom",
        mp4: "https://res.cloudinary.com/dirscuuhp/video/upload/v1765923415/UGC%20Videos/Kiralee_J_cut_prh2ww.mp4",
        hls: "https://res.cloudinary.com/dirscuuhp/video/upload/v1765923415/UGC%20Videos/Kiralee_J_cut_prh2ww.m3u8",
        poster: "https://res.cloudinary.com/dirscuuhp/image/upload/v1743762515/UGC%20Videos/Kiralee_J_f6scbb.webp",
        text: "The symptoms were just insane. I couldn’t explain them, there was no reason for them. Fast forward... I finally found Inner Balance. It’s like I’m waking up from a foggy dream.",
        author: "KIRALEE",
    },
    {
        type: "info",
        text: "In the first couple days that I took it, I had 110% energy back. My husband was like ‘Whoa I haven’t seen this in you in a really long time.’ It just keeps getting better and better.",
        author: "HAYLEY",
        photo: "https://res.cloudinary.com/dirscuuhp/image/upload/v1765282113/new_design/Frame_1171277876_1_xlr0lp.webp",
    },
    {
        type: "video-top",
        mp4: "https://res.cloudinary.com/dirscuuhp/video/upload/v1765923406/UGC%20Videos/Stacey_A_cut_ij2sqs.mp4",
        hls: "https://res.cloudinary.com/dirscuuhp/video/upload/v1765923406/UGC%20Videos/Stacey_A_cut_ij2sqs.m3u8",
        poster: "https://res.cloudinary.com/dirscuuhp/image/upload/v1765924515/UGC%20Videos/Stacey_A_crop_1_oesmb0.webp",
        text: "I can honestly say that after 3 days, I said to my husband... I don’t know how to explain it, but I feel myself again.",
        author: "STACEY",
        smallText: true,
    },
    {
        type: "double-info",
        feedbacks: [
            {
                text: "I already feel a lot of my pain decreasing, I’m sleeping better, and I’m able to mentally focus.",
                author: "ELANA",
                photo: "https://res.cloudinary.com/dirscuuhp/image/upload/v1748268372/UGC%20Videos/Elana_B_s6rynz.jpg",
            },
            {
                text: "My creativity has come back, my brain fog has lifted, I feel more level energy day in and day out.",
                author: "MEGAN",
                photo: "https://res.cloudinary.com/dirscuuhp/image/upload/v1743762514/UGC%20Videos/Megan_R_ffzarx.webp",
            },
        ]
    },
    {
        type: "video-top",
        mp4: "https://res.cloudinary.com/dirscuuhp/video/upload/v1765923396/UGC%20Videos/Jessica_K_cut_s3dcvg.mp4",
        hls: "https://res.cloudinary.com/dirscuuhp/video/upload/v1765923396/UGC%20Videos/Jessica_K_cut_s3dcvg.m3u8",
        poster: "https://res.cloudinary.com/dirscuuhp/image/upload/v1748268372/UGC%20Videos/Jessica_K_axbjjw.webp",
        text: "I absolutely adore Inner Balance. I started it and I feel amazing. It’s been 6 months now. If you’re thinking about it, stop thinking and just do it. You won’t regret it.",
        author: "JESSICA",
        smallText: true,
    },
];
